import { Component, ChangeDetectionStrategy, inject, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { CommonModule } from '@angular/common';
import { MatLegacyButtonModule as MatButtonModule } from '@angular/material/legacy-button';
import { MatIconModule } from '@angular/material/icon';
import { Store, select } from '@ngrx/store';
import { selectZefDialogState, zefDialogClose } from '@zerops/zef/dialog';
import { selectZefNgrxRouterUrl } from '@zerops/zef/ngrx-router';
import { ZefReactiveComponent } from '@zerops/zef/core';
import { ProjectBaseApi } from '@zerops/zerops/core/project-base';
import { combineLatest, EMPTY, fromEvent, Subject } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, filter, map, startWith, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { OverlayTrayService } from '../overlay-tray';
import { FEATURE_NAME } from './victoria-logs-overlay.constant';
import { VictoriaLogsOverlayMeta } from './victoria-logs-overlay.model';
import { VictoriaLogsOverlayService } from './victoria-logs-overlay.service';

@Component({
  selector: 'z-victoria-logs-overlay',
  templateUrl: './victoria-logs-overlay.feature.html',
  styleUrl: './victoria-logs-overlay.feature.scss',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule
  ]
})
export class VictoriaLogsOverlayFeature extends ZefReactiveComponent {

  // # Deps
  #store = inject(Store);
  #sanitizer = inject(DomSanitizer);
  #api = inject(ProjectBaseApi);
  #tray = inject(OverlayTrayService);
  overlay = inject(VictoriaLogsOverlayService);

  // # Event Streams
  onClose$ = new Subject<void>();
  onMinimize$ = new Subject<void>();
  onMaximize$ = new Subject<void>();
  onUnmaximize$ = new Subject<void>();

  // # Data — sync
  key = FEATURE_NAME;
  sanitizedUrl = signal<SafeResourceUrl | null>(null);
  // Raw URL kept alongside sanitizedUrl so the "open in new tab" control can
  // hand a plain string to window.open without unwrapping the SafeResourceUrl.
  rawUrl = signal<string | null>(null);
  projectName = signal('');
  loading = signal(false);
  // Tracks the projectId whose URL is currently loaded, so re-opens for the
  // same project skip the URL fetch and preserve the iframe's internal state.
  #loadedProjectId: string | null = null;

  // # Data — async
  dialogState$ = this.#store.pipe(select(selectZefDialogState(FEATURE_NAME)));
  open$ = this.dialogState$.pipe(map((data) => data?.state));
  // Each dialog-open with a valid projectId emits — guarantees fresh URL fetch
  // on every open, including close→reopen for the same project.
  openMeta$ = this.dialogState$.pipe(
    filter((data) => !!data?.state),
    map((data) => data.meta as VictoriaLogsOverlayMeta | undefined),
    filter((meta): meta is VictoriaLogsOverlayMeta => !!meta?.projectId)
  );
  // True when the user is on the project's /logging subpage — gates two things:
  // (a) bubble suppression (they have the in-page button), (b) compact mode
  // availability (only this page has a trlog to anchor compact size to).
  hideBubble$ = this.#store.pipe(
    select(selectZefNgrxRouterUrl),
    map((url) => (url ?? '').split('?')[0].endsWith('/logging')),
    distinctUntilChanged()
  );
  onLoggingPage = toSignal(this.hideBubble$, { initialValue: false });

  // # State resolver
  state = this.$connect({
    open: this.open$
  });

  // # Dispatcher
  constructor() {
    super();

    this.$dispatchActions([
      // Close
      this.onClose$.pipe(
        tap(() => this.#reset()),
        map(() => zefDialogClose({ key: FEATURE_NAME }))
      ),

      // Minimize / Maximize / Unmaximize — local state only
      this.onMinimize$.pipe(
        tap(() => this.overlay.minimize()),
        map(() => null),
        filter((v) => v !== null)
      ),

      this.onMaximize$.pipe(
        tap(() => this.overlay.maximize()),
        map(() => null),
        filter((v) => v !== null)
      ),

      this.onUnmaximize$.pipe(
        tap(() => this.overlay.unmaximize()),
        map(() => null),
        filter((v) => v !== null)
      ),

      // On each open: mark service open, anchor position, restore minimize.
      // Skip the URL fetch when re-opening the same project — preserves iframe state
      // (so the in-page button = "bring forward", not "destroy and reload").
      this.openMeta$.pipe(
        tap((meta) => {
          this.overlay.open.set(true);
          this.projectName.set(meta.projectName || '');
          this.overlay.updatePosition(meta.rect ?? this.#detectAnchorRect() ?? this.#defaultRect());
          this.overlay.restore();
        }),
        filter((meta) => this.#loadedProjectId !== meta.projectId || !this.sanitizedUrl()),
        tap((meta) => {
          this.loading.set(true);
          this.sanitizedUrl.set(null);
          this.#loadedProjectId = meta.projectId;
        }),
        switchMap((meta) => this.#api.logUrl$(meta.projectId).pipe(
          catchError(() => EMPTY)
        )),
        tap((log) => {
          this.loading.set(false);
          this.rawUrl.set(log?.urlUi ?? null);
          this.sanitizedUrl.set(
            log?.urlUi ? this.#sanitizer.bypassSecurityTrustResourceUrl(log.urlUi) : null
          );
        }),
        map(() => null),
        filter((v) => v !== null)
      ),

      // Re-anchor the iframe when navigating between pages while the overlay is open,
      // so it follows whichever <z-trlog> is on screen.
      this.#store.pipe(
        select(selectZefNgrxRouterUrl),
        distinctUntilChanged(),
        debounceTime(50),
        tap(() => {
          if (this.overlay.open()) {
            this.overlay.updatePosition(this.#detectAnchorRect() ?? this.#defaultRect());
          }
        }),
        map(() => null),
        filter((v) => v !== null)
      ),

      // Re-anchor on window resize. Compact mode stores pixel rects, so without
      // this the overlay would freeze at its original size while the page reflows.
      // Maximized mode is fine — CSS 100vw/100vh tracks the viewport natively.
      fromEvent(window, 'resize').pipe(
        debounceTime(50),
        filter(() => this.overlay.open() && !this.overlay.maximized()),
        tap(() => this.overlay.updatePosition(this.#detectAnchorRect() ?? this.#defaultRect())),
        map(() => null),
        filter((v) => v !== null)
      ),

      // Reset when dialog closes externally
      this.open$.pipe(
        filter((open) => !open),
        tap(() => this.#reset()),
        map(() => null),
        filter((v) => v !== null)
      ),

      // Tray bubble registration follows minimized + hideBubble (suppressed on /logging)
      combineLatest([
        toObservable(this.overlay.minimized),
        this.hideBubble$.pipe(startWith(false))
      ]).pipe(
        tap(([ minimized, hideBubble ]) => {
          if (minimized && !hideBubble) {
            this.#tray.register({
              id: FEATURE_NAME,
              caption: 'VictoriaLogs',
              title: this.projectName() || 'project logs',
              onRestore: () => this.overlay.restore(),
              onClose: () => this.onClose$.next()
            });
          } else {
            this.#tray.unregister(FEATURE_NAME);
          }
        }),
        map(() => null),
        filter((v) => v !== null)
      ),

      // When the overlay becomes visible (open + not minimized) on a non-/logging
      // page, force fullscreen — there's no trlog to anchor compact mode to.
      combineLatest([
        toObservable(this.overlay.open),
        toObservable(this.overlay.minimized)
      ]).pipe(
        map(([ open, minimized ]) => open && !minimized),
        distinctUntilChanged(),
        filter((visible) => visible),
        withLatestFrom(this.hideBubble$.pipe(startWith(false))),
        tap(([ _, onLoggingPage ]) => {
          if (!onLoggingPage) {
            this.overlay.maximize();
          }
        }),
        map(() => null),
        filter((v) => v !== null)
      )
    ]);
  }

  #reset() {
    // Order matters: clear `open` first so the (open && !minimized) "visible"
    // computed never flips true mid-reset and re-triggers the auto-maximize
    // stream while we're tearing down.
    this.overlay.open.set(false);
    this.overlay.reset();
    this.sanitizedUrl.set(null);
    this.rawUrl.set(null);
    this.projectName.set('');
    this.loading.set(false);
    this.#loadedProjectId = null;
  }

  openInNewTab() {
    const url = this.rawUrl();
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  }

  #detectAnchorRect() {
    const el = document.querySelector('z-trlog');
    if (!el) return null;
    const r = el.getBoundingClientRect();
    if (r.width === 0 || r.height === 0) return null;
    return { top: r.top, left: r.left, width: r.width, height: r.height };
  }

  #defaultRect() {
    const w = window.innerWidth;
    const h = window.innerHeight;
    const width = Math.min(1400, Math.round(w * 0.85));
    const height = Math.min(900, Math.round(h * 0.85));
    return {
      top: Math.round((h - height) / 2),
      left: Math.round((w - width) / 2),
      width,
      height
    };
  }
}
